import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import {
  CheckDatabaseLog,
  OrganizationDatabaseLogsStore,
  OrganizationItemStore,
  OrganizationDatabaseStatusItem,
  CsvKeys,
  getTranslatedHeader,
  CheckDatabaseLogResultItem,
} from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, App, Row } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import DatabaseCheckResults from './database-check-results';
import Moment from 'moment';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIdentity } from 'src/core/services/authentication';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import i18next from 'i18next';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { PermissionsConstants } from 'src/common/types';

const notificationIcon = <InfoCircleOutlined style={{ fontSize: 24, color: '#4169E1' }} />;
const permissions = [PermissionsConstants.ManageAdminOrganization];

const OrganizationDbLogs: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const location = useLocation();
  const navigate = useNavigate();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasManageAdminOrganitionsPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'CreatedOn', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const databaseLogsStore = container.get(OrganizationDatabaseLogsStore);
  const databaseLogsState = databaseLogsStore.state;

  const organizationItemStore = container.get(OrganizationItemStore);
  const databaseCheckResults = useRef<{ organizationId: String; displayName: string; isSuccess: boolean; error: string }[]>([]);
  const registeredLogId = useRef('a');
  const { modal, notification } = App.useApp();

  setHeader(t('DatabaseCheckLogs'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organization') },
    { path: 'logs', breadcrumbName: t('OrganizationLogs') },
  ]);

  useEffect(
    () => {
      if (!hasManageAdminOrganitionsPermission) return;
      databaseLogsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!hasManageAdminOrganitionsPermission) {
    navigate('/organizations');
    return null;
  }

  const checkDatabases = async () => {
    notification.info({
      icon: notificationIcon,
      key: 'database-ckeck',
      duration: 0,
      message: (
        <>
          <strong>{t('CheckingDatabases')}</strong>
        </>
      ),
      placement: 'bottomLeft',
      style: { width: '500px' },
    });
    const result = await organizationItemStore.checkAllOrganizationsDatabases();
    if (Array.isArray(result)) {
      databaseCheckResults.current = result.map((value: any) => {
        return { organizationId: value.organizationId, displayName: value.displayName, isSuccess: value.isSuccess, error: value.error };
      });

      const registeredLog = await databaseLogsStore.registerLog({
        success: false,
        checkDbLogResults: result.map((value: any) => {
          return {
            errorMessage: value.error,
            organizationId: value.organizationId,
            organizationName: value.displayName,
            success: value.isSuccess,
          } as CheckDatabaseLogResultItem;
        }),
      });

      if (registeredLog?.identity) {
        registeredLogId.current = registeredLog?.identity;
      }

      databaseLogsStore.load(query);

      showDatabaseCheckResults();
    } else {
      notification.error({
        key: 'database-ckeck',
        message: (
          <>
            <strong>{`${t('Error')}: ${t(result?.message ?? '')}`}</strong>
          </>
        ),
        placement: 'bottomLeft',
        style: { width: '500px' },
      });
    }
  };

  const showDatabaseCheckResults = () => {
    notification.success({
      key: 'database-ckeck',
      message: (
        <>
          <strong>{t('CheckDatabasesComplete')}</strong>
          <br />
          <br />{' '}
          <span
            className="clickable-span strong"
            onClick={() => {
              notification.destroy('database-ckeck');
              modal.info({
                width: 600,
                content: (
                  <>
                    <DatabaseCheckResults
                      logId={registeredLogId.current}
                      t={t}
                      navigate={navigate}
                      data={databaseCheckResults.current}
                      state={location.state}
                      exportItems={databaseCheckResults.current.map((value) => {
                        return {
                          organizationName: value.displayName,
                          result: value.isSuccess ? t('Success') : `Error: ${value.error}`,
                        } as OrganizationDatabaseStatusItem;
                      })}
                      exportKeys={[
                        { key: CsvKeys.OrganizationName, translatedHeader: getTranslatedHeader(CsvKeys.OrganizationName, t) },
                        { key: CsvKeys.Status, translatedHeader: getTranslatedHeader(CsvKeys.Status, t) },
                      ]}
                    />
                  </>
                ),
              });
            }}
          >
            {t('ViewResults')}
          </span>
        </>
      ),
      placement: 'bottomLeft',
      style: { width: '500px' },
    });
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'CreatedOn',
        field: 'CreatedOn',
        title: t('CreatedOn'),
        width: '20%',
        renderer: (data) => {
          const formattedDate = Moment.utc(data.createdOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`);
          return (
            <span>
              <Link to={{ pathname: `db-logs/${data.id}` }} state={{ ...location.state, date: formattedDate }}>
                {formattedDate}
              </Link>
            </span>
          );
        },
      },
      {
        sortable: true,
        searchable: true,
        sortField: 'CreatedBy',
        field: 'CreatedBy',
        title: t('CreatedBy'),
        width: '30%',
        renderer: (data) => <span>{data.userName ? t(data.userName) : ''}</span>,
      },
      {
        useProfile: true,
        field: 'Success',
        title: t('Result'),
        renderer: (data) => <span style={{ color: data.success ? '#53c273' : 'red' }}>{data.success ? `${t('Success')}` : 'Error'}</span>,
      },
    ],
    data: { ...databaseLogsState.value, isBusy: databaseLogsState.value.isBusy },
  } as TableModel<CheckDatabaseLog>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            databaseLogsStore.load(query);
          }}
          onRefresh={() => databaseLogsStore.load(query)}
          model={databaseLogsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            databaseLogsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(databaseLogsState.errorMessage.value) : t('DatabaseLogsTableError'))
          }
          rightToolbar={
            <li>
              <Button type="primary" onClick={checkDatabases}>
                {t('CheckDatabases')}
              </Button>
            </li>
          }
        />
      </Card>
    </Row>
  );
};

export default OrganizationDbLogs;
